import pipefitterApi from '../../../services/pipefitterApi';

/** Slim row for Pipe Inventory Fitter browse (not full getPipeStatusList). */
export type PipeStatusInventoryRow = {
  pipeID: string;
  pipe_id?: string | null;
  groupId?: string | null;
  pipeSize?: number | null;
  wallThickness?: number | null;
  status?: string | null;
  /** End entries with S3 fileUrl only */
  pipeData?: unknown;
};

export type PipeStatusInventoryQuery = {
  projectId?: string | number | null;
  projectNumber?: string | null;
  projectName?: string | null;
  /** Size line / material pipe id filter. */
  pipe_id?: string | null;
  /** When set, return only rows for this group (expand-on-demand). */
  groupId?: string | null;
  /** When true, return group ids only — do not load pipeStatus rows. */
  groupsOnly?: boolean;
  page?: number;
  pageSize?: number;
  search?: string | null;
};

export type PipeStatusInventoryGroup = {
  groupId: string;
};

export type PipeStatusInventoryPage = {
  rows: PipeStatusInventoryRow[];
  totalCount: number;
};

export type PipeStatusInventoryUnavailableError = Error & {
  code: 'PIPE_STATUS_INVENTORY_UNAVAILABLE';
  status?: number;
};

const ROW_KEYS = ['rows', 'records', 'results', 'items', 'data', 'list'] as const;
const TOTAL_KEYS = ['totalCount', 'total', 'count', 'totalRecords'] as const;

export function buildPipeStatusInventoryQuery(query: PipeStatusInventoryQuery): URLSearchParams {
  const params = new URLSearchParams();
  const projectNum = String(query.projectNumber ?? '').trim();
  const projectId = query.projectId;
  const page = Math.max(1, Math.floor(Number(query.page) || 1));
  const pageSize = Math.min(100, Math.max(1, Math.floor(Number(query.pageSize) || 50)));
  const search = String(query.search ?? '').trim();

  if (projectNum) params.set('project_num', projectNum);
  if (projectId != null && String(projectId).trim() !== '') {
    params.set('project_id', String(projectId).trim());
  }
  if (query.projectName?.trim()) {
    params.set('project_name', query.projectName.trim());
  }
  if (query.groupsOnly) {
    params.set('groupsOnly', '1');
  }
  const materialPipeId = String(query.pipe_id ?? '').trim();
  if (materialPipeId) {
    params.set('pipe_id', materialPipeId);
  }
  const groupId = String(query.groupId ?? '').trim();
  if (groupId && groupId !== '—') {
    params.set('groupId', groupId);
  } else if (groupId === '—') {
    params.set('groupId', '');
  }
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));
  if (search) params.set('search', search);
  return params;
}

export function parsePipeStatusInventoryGroupsResponse(body: unknown): PipeStatusInventoryGroup[] {
  const groupsRaw: unknown[] = (() => {
    if (!body || typeof body !== 'object') return [];
    const root = body as Record<string, unknown>;
    const data = root.data;
    if (Array.isArray(root.groups)) return root.groups;
    if (data && typeof data === 'object' && !Array.isArray(data)) {
      const inner = data as Record<string, unknown>;
      if (Array.isArray(inner.groups)) return inner.groups;
    }
    if (Array.isArray(data)) return data;
    return [];
  })();

  return groupsRaw
    .map((g) => {
      if (typeof g === 'string') {
        const raw = g.trim();
        return { groupId: raw || '—' };
      }
      if (!g || typeof g !== 'object') return null;
      const rec = g as Record<string, unknown>;
      const raw = String(rec.groupId ?? rec.group_id ?? '').trim();
      return { groupId: raw || '—' };
    })
    .filter((g): g is PipeStatusInventoryGroup => g != null);
}

export function getPipeStatusInventoryUrl(query: URLSearchParams): string {
  return `/reports/getPipeStatusInventory?${query.toString()}`;
}

function readTotal(obj: Record<string, unknown>): number | undefined {
  for (const key of TOTAL_KEYS) {
    const v = obj[key];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  }
  return undefined;
}

function normalizeRow(raw: unknown): PipeStatusInventoryRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const pipeID = String(r.pipeID ?? r.pipeId ?? r.pipe_no ?? '').trim();
  if (!pipeID) return null;
  return {
    pipeID,
    pipe_id:
      r.pipe_id != null
        ? String(r.pipe_id)
        : r.pipePipeId != null
          ? String(r.pipePipeId)
          : null,
    groupId:
      r.groupId != null
        ? String(r.groupId)
        : r.group_id != null
          ? String(r.group_id)
          : null,
    pipeSize:
      r.pipeSize != null
        ? Number(r.pipeSize)
        : r.size != null
          ? Number(r.size)
          : null,
    wallThickness: r.wallThickness != null ? Number(r.wallThickness) : null,
    status: r.status != null ? String(r.status) : null,
    pipeData: r.pipeData ?? r.pipe_data,
  };
}

function extractRows(body: unknown): PipeStatusInventoryRow[] {
  if (Array.isArray(body)) {
    return body.map(normalizeRow).filter((r): r is PipeStatusInventoryRow => r != null);
  }
  if (!body || typeof body !== 'object') return [];

  const root = body as Record<string, unknown>;
  const candidates: Record<string, unknown>[] = [root];
  if (root.data && typeof root.data === 'object' && !Array.isArray(root.data)) {
    candidates.push(root.data as Record<string, unknown>);
  }

  for (const c of candidates) {
    for (const key of ROW_KEYS) {
      const v = c[key];
      if (Array.isArray(v)) {
        return v.map(normalizeRow).filter((r): r is PipeStatusInventoryRow => r != null);
      }
    }
  }

  if (Array.isArray(root.data)) {
    return root.data.map(normalizeRow).filter((r): r is PipeStatusInventoryRow => r != null);
  }
  return [];
}

export function parsePipeStatusInventoryResponse(body: unknown): PipeStatusInventoryPage {
  const rows = extractRows(body);
  let totalCount = rows.length;

  if (body && typeof body === 'object' && !Array.isArray(body)) {
    const root = body as Record<string, unknown>;
    const fromRoot = readTotal(root);
    const fromData =
      root.data && typeof root.data === 'object' && !Array.isArray(root.data)
        ? readTotal(root.data as Record<string, unknown>)
        : undefined;
    if (fromRoot != null) totalCount = fromRoot;
    else if (fromData != null) totalCount = fromData;
  }

  return { rows, totalCount };
}

function unavailableError(message: string, status?: number): PipeStatusInventoryUnavailableError {
  const err = new Error(message) as PipeStatusInventoryUnavailableError;
  err.code = 'PIPE_STATUS_INVENTORY_UNAVAILABLE';
  err.status = status;
  return err;
}

/** Lazy: load group ids for a project (optional pipe_id) without pipeStatus rows. */
export async function fetchPipeStatusInventoryGroups(
  query: Omit<PipeStatusInventoryQuery, 'groupsOnly' | 'groupId' | 'page' | 'pageSize' | 'search'>
): Promise<PipeStatusInventoryGroup[]> {
  const params = buildPipeStatusInventoryQuery({
    ...query,
    groupsOnly: true,
    page: 1,
    pageSize: 1,
  });
  const url = getPipeStatusInventoryUrl(params);
  try {
    const { data } = await pipefitterApi.get(url);
    return parsePipeStatusInventoryGroupsResponse(data);
  } catch (error: unknown) {
    const status =
      error && typeof error === 'object' && 'response' in error
        ? Number((error as { response?: { status?: number } }).response?.status)
        : undefined;
    if (status === 404 || status === 501 || status === 502) {
      throw unavailableError(
        'Pipe Fitter inventory API is not available yet. Ask backend to deploy getPipeStatusInventory.',
        status
      );
    }
    const message =
      error instanceof Error ? error.message : 'Failed to load Pipe Fitter groups';
    throw unavailableError(message, status);
  }
}

/**
 * Slim inventory browse — never use getPipeStatusList here.
 */
export async function fetchPipeStatusInventory(
  query: PipeStatusInventoryQuery
): Promise<PipeStatusInventoryPage> {
  const params = buildPipeStatusInventoryQuery(query);
  const url = getPipeStatusInventoryUrl(params);

  try {
    const { data } = await pipefitterApi.get(url);
    return parsePipeStatusInventoryResponse(data);
  } catch (error: unknown) {
    const status =
      error && typeof error === 'object' && 'response' in error
        ? Number((error as { response?: { status?: number } }).response?.status)
        : undefined;
    if (status === 404 || status === 501 || status === 502) {
      throw unavailableError(
        'Pipe Fitter inventory API is not available yet. Ask backend to deploy getPipeStatusInventory.',
        status
      );
    }
    const message =
      error instanceof Error ? error.message : 'Failed to load Pipe Fitter inventory';
    throw unavailableError(message, status);
  }
}
